import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    storagesState$,
} from 'models/profile/storages/table';
import {StorageFormattedRow, StorageRawRow} from 'types/profile';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: StorageRawRow[] | undefined,
        t: TFunction,
        onOpenStorage: (storage: StorageRawRow) => void,
    ) => StorageFormattedRow[];
    headerControls?: ReactNode;
    onOpenStorage: (storage: StorageRawRow) => void;
}

export const StoragesGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    onOpenStorage,
}: Props) => {
    const {t} = useTranslation();
    const state = useStore(storagesState$);

    useGate(Gate);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                state.filters,
                filtersChanged,
                filtersReset,
                state.sortings,
                sortingsChanged,
            ),
        [t, state.filters, state.sortings],
    );
    const mappedData = React.useMemo(() => dataMapper(state.data?.data, t, onOpenStorage), [
        dataMapper,
        state.data,
        t,
        onOpenStorage,
    ]);

    return (
        <Table
            columns={columns}
            data={mappedData}
            totalRowsCount={state.data?.totalElements}
            pageCount={state.data?.totalPages}
            pageIndex={state.pageNumber}
            onPageChange={pageNumberChanged}
            isLoading={state.isLoading}
            headerControls={headerControls}
            userConfig={{
                gridName: 'storagesGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
        />
    );
};
